import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaPhone, FaChevronDown } from 'react-icons/fa';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Scroll listener for shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const isActive = (path: string) => location.pathname === path;
  const isPracticeAreaActive = () => location.pathname.startsWith('/practice-areas');

  const practiceAreas = [
    { title: 'Business Law', slug: 'business-law' },
    { title: 'Real Estate Law', slug: 'real-estate' },
    { title: 'Immigration Law', slug: 'immigration' },
    { title: 'Wills & Estates', slug: 'wills-estates' },
  ];

  return (
    <nav className={`nav ${isScrolled ? 'nav--scrolled' : ''}`}>
      <div className="nav__inner">
        <Link to="/" className="nav__logo">
          Janssen Law
        </Link>

        <div className={`nav__links ${isOpen ? 'nav__links--open' : ''}`}>
          <Link
            to="/"
            className={`nav__link ${isActive('/') ? 'nav__link--active' : ''}`}
            onClick={() => setIsOpen(false)}
          >
            Home
          </Link>

          <div
            className={`nav__dropdown ${isPracticeAreaActive() ? 'nav__link--active' : ''}`}
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button
              className="nav__link nav__dropdown-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              Practice Areas <FaChevronDown className={`nav__chevron ${dropdownOpen ? 'nav__chevron--open' : ''}`} />
            </button>
            <div className={`nav__dropdown-menu ${dropdownOpen ? 'nav__dropdown-menu--open' : ''}`}>
              {practiceAreas.map((area) => (
                <Link
                  key={area.slug}
                  to={`/practice-areas/${area.slug}`}
                  className={`nav__dropdown-item ${isActive(`/practice-areas/${area.slug}`) ? 'nav__dropdown-item--active' : ''}`}
                  onClick={() => { setIsOpen(false); setDropdownOpen(false); }}
                >
                  {area.title}
                </Link>
              ))}
            </div>

            {/* Mobile: show practice areas inline */}
            <div className="nav__mobile-subnav">
              {practiceAreas.map((area) => (
                <Link
                  key={area.slug}
                  to={`/practice-areas/${area.slug}`}
                  className="nav__link nav__link--sub"
                  onClick={() => setIsOpen(false)}
                >
                  {area.title}
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/team"
            className={`nav__link ${isActive('/team') ? 'nav__link--active' : ''}`}
            onClick={() => setIsOpen(false)}
          >
            Our Team
          </Link>

          <Link
            to="/about"
            className={`nav__link ${isActive('/about') ? 'nav__link--active' : ''}`}
            onClick={() => setIsOpen(false)}
          >
            About
          </Link>

          <Link
            to="/contact"
            className={`nav__link ${isActive('/contact') ? 'nav__link--active' : ''}`}
            onClick={() => setIsOpen(false)}
          >
            Contact
          </Link>
        </div>

        <a href="tel:+14169291103" className="nav__cta">
          <FaPhone />
          <span>(416) 929-1103</span>
        </a>

        <button
          className={`nav__hamburger ${isOpen ? 'nav__hamburger--open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile overlay */}
      {isOpen && <div className="nav__overlay" onClick={() => setIsOpen(false)} />}
    </nav>
  );
};

export default Navigation;
